import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useCompleteCompanionCoveragesMutation } from '@/services/api/coverageService';
import type {
  CompanionCoveragesReviewResponse,
  CoverageAssociation,
} from '@/services/types/coverage-types';

/**
 * Manages coverage-to-matter association state and the completion mutation
 * for the companion coverages review flow.
 */
export const useCompanionAssociations = (
  data: CompanionCoveragesReviewResponse | undefined,
  matterId: string,
) => {
  const [completeCompanionCoverages, { isLoading: isCompleting }] =
    useCompleteCompanionCoveragesMutation();

  // Track associations as Map<source_coverage_id, Set<target_matter_id>>
  const [associations, setAssociations] = useState<Map<string, Set<string>>>(
    new Map(),
  );

  // Track the result of the last successful completion for inline feedback
  const [completionResult, setCompletionResult] = useState<{
    linksAdded: number;
    linksRemoved: number;
  } | null>(null);

  // Prevent re-seeding on RTK Query refetches (e.g. window refocus)
  const seededRef = useRef(false);

  // Reset seed guard when data is cleared (modal closed / query skipped)
  // so the next open re-seeds from fresh server data.
  useEffect(() => {
    if (!data) {
      seededRef.current = false;
    }
  }, [data]);

  // Initialize associations from primary member's shared_to_matter_ids
  useEffect(() => {
    if (seededRef.current) return;
    if (!data?.members) return;
    const primary = data.members.find((m) => m.is_primary);
    if (!primary) return;
    seededRef.current = true;
    const initial = new Map<string, Set<string>>();
    for (const cov of primary.coverages) {
      if (cov.shared_to_matter_ids && cov.shared_to_matter_ids.length > 0) {
        initial.set(cov.id, new Set(cov.shared_to_matter_ids));
      }
    }
    setAssociations(initial);
  }, [data?.members]);

  const handleToggleAssociation = useCallback(
    (coverageId: string, targetMatterId: string, checked: boolean) => {
      setCompletionResult(null);
      setAssociations((prev) => {
        const next = new Map(prev);
        const matterIds = new Set(next.get(coverageId) ?? []);
        if (checked) {
          matterIds.add(targetMatterId);
        } else {
          matterIds.delete(targetMatterId);
        }
        if (matterIds.size > 0) {
          next.set(coverageId, matterIds);
        } else {
          next.delete(coverageId);
        }
        return next;
      });
    },
    [],
  );

  const handleComplete = useCallback(async () => {
    const flatAssociations: CoverageAssociation[] = [];
    associations.forEach((targetMatterIds, coverageId) => {
      targetMatterIds.forEach((targetId) => {
        flatAssociations.push({
          source_coverage_id: coverageId,
          target_matter_id: targetId,
        });
      });
    });

    try {
      const primaryMatterId = data?.primary_matter_id ?? matterId;
      const result = await completeCompanionCoverages({
        matterId: primaryMatterId,
        body: {
          associations: flatAssociations,
        },
      }).unwrap();

      toast.success(
        `Coverage sharing complete: ${result.links_added ?? 0} added, ${result.links_removed ?? 0} removed`,
      );

      setCompletionResult({
        linksAdded: result.links_added ?? 0,
        linksRemoved: result.links_removed ?? 0,
      });

      // Allow re-seeding from refetched data after cache invalidation
      seededRef.current = false;
    } catch {
      toast.error('Failed to complete coverage sharing');
    }
  }, [associations, data?.primary_matter_id, matterId, completeCompanionCoverages]);

  const clearCompletionResult = useCallback(() => setCompletionResult(null), []);

  return {
    associations,
    isCompleting,
    completionResult,
    clearCompletionResult,
    handleToggleAssociation,
    handleComplete,
  };
};
